import React, { useState, useEffect } from 'react';
import './style.css';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from '../../axiosInstance';
import Grid from 'apollo-react/components/Grid';
import Button from 'apollo-react/components/Button';
import Typography from '../common/StyledTypography';
import Box from 'apollo-react/components/Box';
import OTP from '../common/Form/OTPInput';
import { toast } from 'react-toastify';
import AuthLayout from './AuthLayout';
import { useTranslation } from 'react-i18next';

const decodeTokenEmail = (token) => {
  if (!token) return '';
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.email || '';
  } catch {
    return '';
  }
};

const maskEmail = (email) => {
  if (!email) return '*******@example.com';
  const [username, domain] = email.split('@');
  if (username.length <= 2) {
    return `${username.charAt(0)}*@${domain}`;
  }
  const maskedUsername =
    username.charAt(0) + '*'.repeat(username.length - 2) + username.slice(-1);
  return `${maskedUsername}@${domain}`;
};

const OTPVerification = ({ navigate, verificationToken }) => {
  const { t } = useTranslation();

  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [canResend, setCanResend] = useState(true);
  const [timerId, setTimerId] = useState(null);
  const [token, setToken] = useState(verificationToken || '');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    // Try to get email from the URL token first, then fall back to cookie-based check
    const emailFromToken = decodeTokenEmail(token);
    if (emailFromToken) {
      setUserEmail(emailFromToken);
    } else {
      axios
        .get('/api/auth/check')
        .then((response) => {
          if (response.data?.email) {
            setUserEmail(response.data.email);
          }
        })
        .catch(() => {});
    }
  }, [token]);

  // Cleanup timer on component unmount
  useEffect(() => {
    return () => {
      if (timerId) clearInterval(timerId);
    };
  }, [timerId]);

  const handleVerify = async (e) => {
    e.preventDefault();
    setOtpError('');
    setIsSubmitting(true);

    try {
      const payload = { otp };
      if (token) payload.token = token;

      const resp = await axios.post('/api/auth/verify-otp', payload);

      if (resp.data.success) {
        toast.success(t('otp.verifySuccess'));
        setToken('');
        setTimeout(() => {
          navigate(resp.data.redirectTo || '/login');
        }, 1000);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || t('otp.verifyError');
      toast.error(errorMessage);
      setOtpError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendOTP = async () => {
    setIsResending(true);
    setOtpError('');
    setCanResend(false);

    try {
      const payload = {};
      if (token) payload.token = token;

      const resp = await axios.post('/api/auth/resend-otp', payload);

      if (resp.data.success) {
        toast.success(t('otp.resendSuccess'));
        setOtp('');

        // Update token if a new one was returned
        if (resp.data.verificationToken) {
          setToken(resp.data.verificationToken);
        }

        // Start countdown
        setCountdown(
          // eslint-disable-next-line no-undef
          parseInt(process.env.REACT_APP_OTP_RESEND_COUNTDOWN) || 60
        );
        const timer = setInterval(() => {
          setCountdown((prev) => {
            if (prev <= 1) {
              clearInterval(timer);
              setCanResend(true);
              setTimerId(null);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
        setTimerId(timer);
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || t('otp.resendError');
      toast.error(errorMessage);
      setOtpError(errorMessage);
      setCanResend(true); // Re-enable button on error
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthLayout>
      <Grid
        item
        justifyContent="center"
        width="463px"
        position="relative"
        zIndex={1}
      >
        <Grid container justifyContent="left" className="signInTitleGrid">
          <div>
            <h2 className="signInTitle">{t('otp.title')}</h2>
            <p className="signInDesc signInDescOtp">
              {t('otp.description', {
                email: maskEmail(userEmail),
              })}
            </p>
          </div>
        </Grid>

        <Grid container className="formContainer">
          <Grid container justifyContent="center" sx={{ width: '100%' }}>
            <Grid size={12}>
              <form noValidate onSubmit={handleVerify}>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 2,
                    justifyContent: 'center',
                  }}
                >
                  <OTP
                    separator={<span> </span>}
                    value={otp}
                    onChange={setOtp}
                    length={6}
                  />
                  {otpError && (
                    <Typography
                      variant="bodySmall"
                      style={{
                        color: 'red',
                        textAlign: 'center',
                      }}
                    >
                      {otpError}
                    </Typography>
                  )}
                </Box>
                <Button
                  variant="primary"
                  className="loginButton"
                  type="submit"
                  disabled={isSubmitting}
                >
                  {t('otp.confirmButton')}
                </Button>

                {/* Resend OTP Button */}
                <Button
                  variant="secondary"
                  style={{
                    width: '100%',
                    padding: '10px',
                    textDecoration: canResend ? 'underline' : 'none',
                    background: 'transparent',
                    border: 'none',
                    color: canResend ? '#015FF1' : '#999',
                    opacity: canResend ? 1 : 0.6,
                    cursor: canResend ? 'pointer' : 'not-allowed',
                  }}
                  onClick={handleResendOTP}
                  disabled={isResending || !canResend}
                >
                  {isResending
                    ? t('otp.resending')
                    : !canResend && countdown > 0
                      ? t('otp.resendCountdown', {
                          seconds: countdown,
                        })
                      : t('otp.resendPrompt')}
                </Button>
              </form>
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </AuthLayout>
  );
};

export default function OTPVerificationWithNavigate() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const verificationToken = searchParams.get('token');

  return (
    <OTPVerification
      navigate={navigate}
      verificationToken={verificationToken}
    />
  );
}
